import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tag, Plus, Pencil, Trash2, Check, X, Search, Loader2 } from "lucide-react";

export function BrandsPanel({ canManage }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editName, setEditName] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/products/brands/stats").then((r) => setBrands(r.data.brands || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const nm = newName.trim();
    if (!nm) return toast.error("Marka adı girin");
    setAdding(true);
    try { await api.post("/products/brands", { name: nm }); toast.success(`"${nm}" markası eklendi`); setNewName(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Eklenemedi"); }
    finally { setAdding(false); }
  };
  const rename = async (old) => {
    const nm = editVal.trim();
    if (!nm) return toast.error("Yeni ad girin");
    try { const { data } = await api.put(`/products/brands/${encodeURIComponent(old)}`, { name: nm }); toast.success(`Yeniden adlandırıldı · ${data.renamed} ürün güncellendi`); setEditName(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
  };
  const del = async (b) => {
    if (!window.confirm(`"${b.name}" markası silinsin mi?${b.count ? ` ${b.count} ürün 'Markasız' yapılacak.` : ""}`)) return;
    try { const { data } = await api.delete(`/products/brands/${encodeURIComponent(b.name)}`); toast.success(`"${b.name}" silindi${data.cleared ? ` · ${data.cleared} ürün markasız` : ""}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Silinemedi"); }
  };

  const filtered = brands.filter((b) => b.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="space-y-3 rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="brands-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="brand-panel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marka ara..." className="pl-8" />
        </div>
        {canManage && (
          <div className="flex items-center gap-1.5">
            <Input data-testid="brand-panel-new" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Yeni marka (örn. Groot)" className="w-48" />
            <Button data-testid="brand-panel-add" onClick={add} disabled={adding} className="bg-brand-green text-white hover:bg-brand-green-600">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Ekle</>}
            </Button>
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Markaları buradan ekleyin, yeniden adlandırın veya silin. Yeniden adlandırma tüm ürünlerde otomatik güncellenir; silme ilgili ürünleri "Markasız" yapar.</p>
      {loading ? (
        <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400" data-testid="brands-empty">Marka yok. Yukarıdan ekleyebilirsiniz.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {filtered.map((b) => (
            <div key={b.name} data-testid={`brand-row-${b.name}`} className="flex items-center gap-2 py-2">
              <Tag className="h-4 w-4 shrink-0 text-brand-green" />
              {editName === b.name ? (
                <>
                  <Input data-testid={`brand-edit-input-${b.name}`} autoFocus value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(b.name)} className="h-8 flex-1" />
                  <button data-testid={`brand-edit-save-${b.name}`} onClick={() => rename(b.name)} className="text-brand-green hover:text-emerald-600" title="Kaydet"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditName(null)} className="text-slate-400 hover:text-slate-600" title="Vazgeç"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 truncate text-sm font-medium text-slate-800">{b.name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{b.count} ürün</span>
                  {canManage && (
                    <>
                      <button data-testid={`brand-edit-${b.name}`} onClick={() => { setEditName(b.name); setEditVal(b.name); }} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100" title="Yeniden adlandır"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`brand-del-${b.name}`} onClick={() => del(b)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BrandsPanel;
